// pageContext.js — builds a small JSON snapshot of the current page state
// to send to the LLM. Never dump the entire data.json — keep it board-focused.

const SAMPLES_PER_LIST = 8;

export function buildContext(pageId, { data, agg, attainment, resultsChain, beneficiaries, filters, filtered }) {
  if (!data || !agg) return {};

  const base = {
    programme: {
      totalProjects: data.kpi.totalProjects,
      hrdp: data.kpi.hrdp,
      fdp: data.kpi.fdp,
      totalBudgetCr: data.kpi.totalBudgetCr,
      overall: data.meta.programmeOverall,
      band: data.kpi.programmeBand,
      partners: data.kpi.partners,
      states: data.kpi.states,
      totalResponses: data.meta.totalResponses,
      realCriteria: data.meta.realCriteria,
      derivedCriteria: data.meta.derivedCriteria,
      weights: data.meta.weights,
    },
    filters: {
      type: filters.type,
      regions: [...filters.regions],
      bands: [...filters.bands],
      filteredCount: filtered?.length ?? 0,
    },
    filteredAgg: {
      overall: agg.po,
      band: agg.poBand,
      n: agg.n,
      hrdp: agg.hrdp,
      fdp: agg.fdp,
      budgetCr: agg.totalBudget,
      partnersCount: agg.partnersCount,
      statesCount: agg.statesCount,
      critAvg: agg.critAvg,
      hrdpVsFdp: agg.hf,
      tools: Object.fromEntries(Object.entries(agg.tiles).map(([k, v]) => [k, { score: v.score, band: v.band, projects: v.projects }])),
    },
  };

  if (pageId === 'overview') {
    return {
      ...base,
      topProjects: agg.projectsSorted.slice(0, 8).map((p) => ({
        code: p.code, partner: p.partner, state: p.state, type: p.type, overall: p.overall,
      })),
      regionPerformance: agg.regionPerf.map((r) => ({ region: r.region, n: r.n, avgScore: r.avgScore })),
      budgetRange: agg.budgetRange,
    };
  }

  if (pageId === 'param') {
    return {
      ...base,
      matrix: (data.matrix || []).map((r) => ({
        criterion: r.criterion, weight: r.weight, overall: r.overall,
        cells: Object.fromEntries(Object.entries(r.cells || {}).map(([t, c]) => [t, c?.score])),
      })),
    };
  }

  if (pageId === 'oecd') {
    return {
      ...base,
      weightSummary: data.weightSummary,
      heatmap: data.heatmap.map((h) => ({ tool: h.tool, label: h.label, overall: h.overall, cells: h.cells })),
    };
  }

  if (pageId === 'geo') {
    return {
      ...base,
      states: agg.states.slice(0, 20).map((s) => ({
        state: s.state, projects: s.projects, avgScore: s.avgScore, band: s.band,
        partner: s.partner, region: s.region, budgetCr: s.budgetCr,
        tools: s.tools,
      })),
    };
  }

  if (pageId === 'partner') {
    return {
      ...base,
      partners: agg.partners.slice(0, 20).map((p) => ({
        partner: p.partner, projects: p.projects, avgScore: p.avgScore, band: p.band,
        budgetCr: p.budgetCr, states: p.states, type: p.type,
        impact: p.impact, sustainability: p.sustainability,
      })),
    };
  }

  if (pageId === 'region') {
    return {
      ...base,
      regionAgg: agg.regionAgg.map((r) => ({ unit: r.unit, projects: r.projects, avgScore: r.avgScore, band: r.band, tools: r.tools })),
      zoneAgg: agg.zoneAgg.map((z) => ({ unit: z.unit, projects: z.projects, avgScore: z.avgScore, band: z.band, tools: z.tools, members: z.members })),
    };
  }

  if (pageId === 'budget') {
    return {
      ...base,
      interventions: data.interventions,
      themeStrength: data.themeStrength,
      correlation: data.correlation,
      scatterSample: agg.scatter.slice(0, SAMPLES_PER_LIST),
    };
  }

  if (pageId === 'results') {
    return {
      ...base,
      resultsChain: resultsChain ? {
        inputs: resultsChain.inputs,
        activities: resultsChain.activities,
        outputs: resultsChain.outputs,
        outcomes: resultsChain.outcomes,
        impact: resultsChain.impact,
      } : null,
    };
  }

  return base;
}

export const SAMPLE_QUESTIONS = {
  overview: [
    'Which region is dragging the programme average down?',
    'How does HRDP compare with FDP across the four thematic tools?',
    'What share of the ₹497 Cr is in the 5+ Cr band and how do those projects score?',
    'Which are the top 3 projects, and what do they share in common?',
  ],
  param: [
    'On which OECD criterion do we score lowest, and why does that matter?',
    'Which thematic tool is strongest on Sustainability?',
    'Where does the weighted matrix suggest the biggest improvement lever?',
  ],
  oecd: [
    'Which criterion contributes the most to the 3.84 overall?',
    'How different are HRDP and FDP on Impact vs Effectiveness?',
    'Which criteria are computed from real data vs modelled placeholders?',
  ],
  geo: [
    'Which top 5 states drive our best scores?',
    'Where are our weakest states, and what tools underperform there?',
    'Which state has the highest budget concentration?',
  ],
  partner: [
    'Who are the 3 highest-spend partners with sub-Good scores?',
    'Which partner has the best Impact + Sustainability combination?',
    'How concentrated is our budget across the top 5 partners?',
  ],
  region: [
    'Which HDFC zone is the top performer, and by how much?',
    'How does the North East compare with the South on Health & Hygiene?',
    'Which region has the widest spread between best and worst project?',
  ],
  budget: [
    'Is there any correlation between budget and OECD score?',
    'Which intervention type has the strongest score across projects?',
    'Where would you increase or reduce spend based on the current data?',
  ],
  results: [
    'Which output pillar is closest to its target, and which is farthest?',
    'Which outcomes have already been met?',
    'How does the Impact stage compare to the OECD verdict?',
  ],
};
